import React from 'react';
import { Avatar as MuiAvatar, SxProps } from '@mui/material';
import { PounceStyleProps, extractPounceProps, mapPouncePropsToSx } from '../../utils';

export interface ChipAvatarProps
  extends
    Omit<React.HTMLAttributes<HTMLDivElement>, keyof PounceStyleProps | 'sx'>,
    PounceStyleProps {
  /** Image URL for the avatar's `<img>`. */
  src?: string;
  /** Responsive image set passed through to the underlying `<img>`. */
  srcSet?: string;
  /** Accessible name when `src` is provided. */
  alt?: string;
  /**
   * Fallback content shown when `src` is missing or fails to load.
   * Typically initials (e.g. `"GM"`) or a small icon.
   */
  children?: React.ReactNode;
  /** Forwarded to the underlying MUI Avatar for advanced styling. */
  sx?: SxProps;
}

/**
 * Circular avatar slot for chip components.
 *
 * Renders a full-bleed circular `Avatar` sized via the
 * `--chip-avatar-size` CSS custom property, which the parent
 * `StaticChip` / `InteractiveChip` sets per size tier. Drop in
 * alongside `<ChipLabel>` when composing a chip with an avatar.
 * Accepts pounceV2 style props (`mr`, `mt`, …) and the standard
 * `sx` escape hatch.
 *
 * @example
 * ```tsx
 * <InteractiveChip variant="primary" onClick={openProfile}>
 *   <ChipAvatar src={user.avatarUrl} alt="" />
 *   <ChipLabel>{user.name}</ChipLabel>
 * </InteractiveChip>
 * ```
 */
export const ChipAvatar = React.forwardRef<HTMLDivElement, ChipAvatarProps>(function ChipAvatar(
  { src, srcSet, alt, children, sx, ...rest },
  ref,
) {
  const { pounceProps, otherProps } = extractPounceProps(rest);
  const pounceStyles = mapPouncePropsToSx(pounceProps);
  return (
    <MuiAvatar
      ref={ref}
      src={src}
      srcSet={srcSet}
      alt={alt}
      variant="circular"
      sx={
        {
          width: 'var(--chip-avatar-size, 1em)',
          height: 'var(--chip-avatar-size, 1em)',
          fontSize: 'calc(var(--chip-avatar-size, 1em) * 0.5)',
          flexShrink: 0,
          ...pounceStyles,
          ...sx,
        } as SxProps
      }
      {...otherProps}
    >
      {children}
    </MuiAvatar>
  );
});
